import { useState, useRef, useEffect, ReactNode } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';
import { ICONS } from '@/constants/icons';

interface DropdownMenuItem {
  label: string;
  icon?: string;
  customIcon?: ReactNode; // For FontAwesome or other custom icons
  onClick: () => void;
  disabled?: boolean;
  show?: boolean; // For conditional rendering based on role
}

interface DropdownMenuProps {
  label: string;
  items: DropdownMenuItem[];
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Which edge of the trigger the panel aligns to (default right: panel extends leftwards) */
  align?: 'left' | 'right';
}

/**
 * DropdownMenu Component
 *
 * A button that opens a dropdown menu with action items.
 */
export const DropdownMenu = ({
  label,
  items,
  disabled = false,
  variant = 'secondary',
  size = 'sm',
  className,
  align = 'right',
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter items based on show property (default true if not specified)
  const visibleItems = items.filter(item => item.show === true || item.show === undefined);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant={variant}
        size={size}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center justify-center gap-2 ${className || ''}`}
      >
        {label}
        <Icon
          name={ICONS.ANGLE_DOWN}
          size="sm"
          ariaHidden
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </Button>

      {isOpen && (
        <div className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-30`}>
          {visibleItems.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                item.onClick();
                setIsOpen(false);
              }}
              disabled={item.disabled}
              className={`
                w-full px-4 py-2 text-left text-sm flex items-center gap-2
                ${item.disabled
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100'
                }
              `}
            >
              {item.customIcon ? item.customIcon : item.icon && <Icon name={item.icon} size="sm" ariaHidden />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
